import { useState } from "react";
import type { FormEvent } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/db7lm57idngsrf4ehmng";

const contactItems = [
  { icon: "ri-mail-line", label: "邮箱", value: "contact@51baoliao.example", note: "工作时间 24 小时内回复" },
  { icon: "ri-customer-service-2-line", label: "客服热线", value: "400-000-5151", note: "周一至周五 09:00 - 18:00" },
  { icon: "ri-map-pin-line", label: "办公地址", value: "北京市朝阳区新闻大厦 18 层", note: "到访请提前预约" },
  { icon: "ri-time-line", label: "爆料值守", value: "7×24 小时在线", note: "突发事件优先处理" },
];

const issueTypes = ["爆料线索", "内容纠错", "版权问题", "合作洽谈", "其他反馈"];

export default function ContactPage() {
  usePageMeta({
    title: "联系我们 - 51爆料网",
    description:
      "联系51爆料网：邮箱、客服热线、办公地址与在线反馈表单。欢迎提供爆料线索、内容纠错与合作咨询。",
    keywords: "51爆料网,联系我们,爆料线索,反馈表单,客服,合作",
  });

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();

      let parsed:
        | { code?: string; message?: string; meta?: { message?: string; detail?: string } }
        | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.length > 0 && serverMsg.length < 200
            ? serverMsg
            : "提交失败，请稍后重试，或直接通过邮箱联系我们。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试或通过邮箱联系我们。");
    }
  };

  const fieldCls =
    "w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400";

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-5 md:px-6 md:py-6">
          <Breadcrumb items={[{ label: "联系我们" }]} />
          <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-3xl">联系我们</h1>
          <p className="mt-2 max-w-3xl text-sm text-foreground-500">
            有线索、有疑问、想合作，都可以在这里找到我们。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-12">
        <div className="flex flex-col gap-8 lg:flex-row">
          <div className="w-full shrink-0 space-y-6 lg:w-[360px]">
            {contactItems.map((item) => (
              <div
                key={item.label}
                className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-foreground-400">{item.label}</p>
                  <p className="mt-0.5 break-words text-sm font-semibold text-foreground-900">{item.value}</p>
                  <p className="mt-1 text-xs text-foreground-400">{item.note}</p>
                </div>
              </div>
            ))}

            <div className="rounded-lg border border-background-200 bg-background-50 p-5 text-center">
              <p className="text-sm font-semibold text-foreground-900">客服微信</p>
              <div className="mx-auto mt-3 h-40 w-40 overflow-hidden rounded-lg bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Clean%20minimal%20wechat%20style%20QR%20code%20illustration%20on%20soft%20light%20background%2C%20simple%20geometric%20black%20modules%2C%20flat%20design%2C%20high%20detail&width=400&height=400&seq=contact-wechat-qr&orientation=squarish"
                  alt="客服微信二维码（示意）"
                  title="51爆料网 客服微信"
                  className="h-full w-full object-cover"
                />
              </div>
              <p className="mt-3 text-xs text-foreground-400">扫码添加客服微信（示意图，仅作展示）</p>
            </div>
          </div>

          <div className="min-w-0 flex-1 rounded-lg border border-background-200 bg-background-100 p-5 md:p-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950">反馈表单</h2>
            <p className="mt-2 text-sm text-foreground-500">
              请尽量描述清楚时间、地点与事件经过，便于我们核实。我们承诺保护你的隐私。
            </p>

            {status === "success" && (
              <div className="mt-5 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
                  <i className="ri-checkbox-circle-fill" />
                </span>
                提交成功，感谢你的反馈！我们会尽快与处理，如需回复请留意你留下的联系方式。
              </div>
            )}

            {status === "error" && (
              <div className="mt-5 flex items-start gap-2 rounded-md border border-accent-300 bg-accent-50 px-4 py-3 text-sm text-accent-800">
                <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
                  <i className="ri-error-warning-fill" />
                </span>
                {formError}
              </div>
            )}

            <form
              id="contact-feedback-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-6 space-y-5"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-700">
                    姓名 <span className="text-accent-600">*</span>
                  </label>
                  <input id="name" name="name" type="text" required placeholder="请输入你的称呼" className={fieldCls} />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-700">
                    联系方式 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="手机号或邮箱，便于回复"
                    className={fieldCls}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="type" className="mb-1.5 block text-sm font-medium text-foreground-700">
                  问题类型
                </label>
                <select id="type" name="type" className={fieldCls} defaultValue={issueTypes[0]}>
                  {issueTypes.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="content" className="mb-1.5 block text-sm font-medium text-foreground-700">
                  详细内容 <span className="text-accent-600">*</span>
                </label>
                <textarea
                  id="content"
                  name="content"
                  required
                  rows={6}
                  maxLength={500}
                  placeholder="请描述爆料线索或反馈内容，不超过 500 字…"
                  className={`${fieldCls} resize-none`}
                />
                <p className="mt-1.5 text-xs text-foreground-400">最多 500 字</p>
              </div>

              <div className="form-honeypot-field" aria-hidden="true">
                <label htmlFor="website_alt">请勿填写此栏</label>
                <input
                  id="website_alt"
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              <div className="flex flex-wrap items-center gap-3 border-t border-background-200 pt-5">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
                  {status === "submitting" ? "提交中…" : "提交反馈"}
                </button>
                <span className="text-xs text-foreground-400">提交即表示你已阅读并同意隐私保护说明</span>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}